import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ChatCircleDots, Tag, ArrowsClockwise, ShieldCheck, Bank } from "@phosphor-icons/react";
import { SiStripe, SiQuickbooks } from "react-icons/si";
import { PeriodToggle, PriceTag } from "@/components/billing/PeriodToggle";
import { HeroChart } from "@/components/landing/HeroChart";
import { usePageMeta, PAGE_META } from "@/lib/pageMeta";
import { HERO_COPY, cachedWinner, fetchLanding, heroVariant, trackHero } from "@/lib/heroExperiment";
import { QuotesBlock } from "@/components/landing/QuotesBlock";
import { SiteNav, SiteFooter } from "@/components/landing/SiteChrome";
import { SecurityBlock } from "@/components/landing/SecurityBlock";
import { Faq } from "@/components/landing/Faq";
import { Walkthrough } from "@/components/landing/Walkthrough";

const IMG = (id, w) => `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=70&w=${w}`;
const HERO_ID = "photo-1764545973653-94c40d993495";
const AI_ID = "photo-1710957987034-cea509422852";
const srcSet = (id) => [640, 1024, 1600].map((w) => `${IMG(id, w)} ${w}w`).join(", ");

const Cta = ({ testid, className = "" }) => (
  <Link to="/sign-up" data-testid={testid} onClick={() => trackHero("cta")}
    className={`inline-flex items-center gap-2 rounded-full bg-[#00FF9D] px-7 py-3.5 font-semibold text-[#050505] transition-[transform,box-shadow] duration-200 hover:scale-[1.03] hover:shadow-[0_0_32px_rgba(0,255,157,0.45)] focus:ring-2 focus:ring-[#00FF9D] focus:outline-none ${className}`}>
    Start 14-Day Free Trial <ArrowRight weight="bold" />
  </Link>
);

const Hero = ({ copy, ready }) => (
  <section className="relative overflow-hidden border-b border-[#2A2A2A] grain">
    <img src={IMG(HERO_ID, 1600)} srcSet={srcSet(HERO_ID)} sizes="100vw" width="1600" height="1067" fetchPriority="high" decoding="async"
      alt="Founder workspace with financial charts on screen" className="absolute inset-0 h-full w-full object-cover opacity-40" />
    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
    <div className="relative mx-auto max-w-7xl px-6 pt-12 pb-16 sm:pt-24 sm:pb-20 lg:pt-32 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6 fade-up">
        <div className={`transition-opacity duration-300 ${ready ? "opacity-100" : "opacity-0"}`}>
        <p data-testid="hero-eyebrow" className="font-mono text-[11px] sm:text-xs tracking-widest text-[#00FF9D] mb-5 sm:mb-6 flex items-center gap-2">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#00FF9D] animate-pulse" /> {copy.eyebrow}
        </p>
        <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02]">{copy.h1}</h1>
        </div>
        <p className="mt-5 sm:mt-7 max-w-xl text-base md:text-lg text-[#A3A3A3] leading-relaxed">
          Sync Stripe, QBO and your bank into one precise runway model. Test every growth move with AI before you make it, and know exactly how far your cash will carry you. No more exported Excel models.
        </p>
        <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
          <Cta testid="hero-cta-btn" />
          <a href="#walkthrough" data-testid="hero-see-dashboard-btn" className="inline-flex items-center gap-2 rounded-full border border-[#2A2A2A] px-6 py-3.5 text-sm font-semibold hover:border-white transition-colors">See the dashboard</a>
        </div>
        <p className="mt-5 font-mono text-xs text-[#A3A3A3] flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>No card required · 3-minute setup</span>
          <Link to="/security" data-testid="hero-security-link" className="inline-flex items-center gap-1 underline underline-offset-4 hover:text-white transition-colors">
            <ShieldCheck className="text-[#00FF9D]" /> How we handle your data
          </Link>
        </p>
      </div>
      <div className="lg:col-span-6 fade-up" style={{ animationDelay: "0.15s" }}>
        <HeroChart />
      </div>
    </div>
  </section>
);

const Features = () => (
  <section id="features" className="mx-auto max-w-7xl px-6 py-24 md:py-32 scroll-mt-16">
    <p className="font-mono text-xs text-[#00FF9D] mb-4">// WHAT YOU GET</p>
    <h2 className="font-display text-3xl md:text-4xl font-semibold max-w-2xl">One calibrated model. Three sources of truth. Room to grow.</h2>
    <div className="mt-16 grid md:grid-cols-6 border-t border-l border-[#2A2A2A]">
      <div data-testid="feature-tri-sync" className="md:col-span-4 p-8 sm:p-10 border-r border-b border-[#2A2A2A] hover:bg-[#121212] transition-colors">
        <ArrowsClockwise size={28} className="text-[#00FF9D]" />
        <h3 className="font-display text-2xl mt-6">Real-time Multi-Sync (Stripe + Bank + QBO)</h3>
        <p className="mt-3 text-[#A3A3A3] max-w-lg">Revenue from Stripe, cash from your bank, expenses, A/R and A/P from QuickBooks. Reconciled and recalibrated into one live cash position every time you open the dashboard.</p>
        <div className="mt-10 flex flex-wrap gap-4">
          {[[SiStripe, "Stripe", "#635BFF"], [Bank, "Bank", "#00FF9D"], [SiQuickbooks, "QuickBooks", "#2CA01C"]].map(([I, n, c]) => (
            <div key={n} className="flex items-center gap-2 rounded-full border border-[#2A2A2A] px-4 py-2 text-sm">
              <I size={16} style={{ color: c }} /> {n}
            </div>
          ))}
        </div>
      </div>
      <div data-testid="feature-categorization" className="md:col-span-2 p-8 sm:p-10 border-r border-b border-[#2A2A2A] hover:bg-[#121212] transition-colors">
        <Tag size={28} className="text-[#00FF9D]" />
        <h3 className="font-display text-2xl mt-6">Automated Expense Categorization</h3>
        <p className="mt-3 text-[#A3A3A3]">Every vendor sorted into Payroll, Software, Infra and more. Toggle a vendor off to fine-tune burn and see the runway you'd gain.</p>
      </div>
      <div data-testid="feature-chat" className="md:col-span-6 relative overflow-hidden p-8 sm:p-10 md:p-14 border-r border-b border-[#2A2A2A]">
        <img src={IMG(AI_ID, 1200)} srcSet={srcSet(AI_ID)} sizes="100vw" width="1200" height="800" loading="lazy" decoding="async"
          alt="Abstract purple data visualization" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        <div className="relative max-w-xl">
          <ChatCircleDots size={28} className="text-[#9D00FF]" />
          <h3 className="font-display text-2xl md:text-3xl mt-6">AI Scenario Calibrator</h3>
          <p className="mt-3 text-[#A3A3A3]">Ask "What happens if I hire two engineers in Q3?" and watch a purple scenario line recalibrate your runway in seconds. Compare growth plans side by side before you commit.</p>
          <div className="mt-8 rounded-xl border border-[#9D00FF]/40 bg-black/70 p-4 font-mono text-sm">
            <span className="text-[#9D00FF]">&gt;</span> What if we lose our biggest customer next month?
          </div>
          <p data-testid="calibrator-provenance" className="mt-4 flex items-start gap-2 text-sm text-[#CFCFCF]">
            <ShieldCheck size={18} className="text-[#00FF9D] shrink-0 mt-0.5" />
            Every calibration shows the numbers it changed, and nothing touches your books without your approval.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const PLANS = [
  { id: "starter", name: "Starter", price: 29, blurb: "For pre-seed founders calibrating every dollar.",
    perks: ["Multi-Sync dashboard", "Zero Cash Date tracking", "25 AI calibrations / day", "Expense categorization"] },
  { id: "pro", name: "Pro", price: 79, blurb: "For seed+ teams scaling hires and raises.", featured: true,
    perks: ["Everything in Starter", "100 AI calibrations / day", "Vendor cut-off modeling", "Multi-bank accounts", "Priority support"] },
];

const Pricing = () => {
  const [period, setPeriod] = useState("monthly");
  return (
    <section id="pricing" className="border-t border-[#2A2A2A] bg-[#0A0A0A] scroll-mt-16">
      <div className="mx-auto max-w-7xl px-6 py-24 md:py-32 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="font-mono text-xs text-[#00FF9D] mb-4">// PRICING</p>
          <h2 className="font-display text-3xl md:text-4xl font-semibold">Cheaper than one hour of a fractional CFO.</h2>
          <p className="mt-5 text-[#A3A3A3]">Both plans start with a 14-day free trial. Cancel anytime. Pay yearly and save 10%.</p>
          <div className="mt-6"><PeriodToggle value={period} onChange={setPeriod} testid="pricing-period-toggle" /></div>
        </div>
        <div className="lg:col-span-8 grid md:grid-cols-2 gap-6">
          {PLANS.map((p) => (
            <div key={p.id} data-testid={`pricing-card-${p.id}`}
              className={`rounded-2xl border p-6 sm:p-8 flex flex-col transition-[transform,border-color] duration-200 hover:-translate-y-1 ${p.featured ? "border-[#00FF9D]/60 bg-[#00FF9D]/[0.03]" : "border-[#2A2A2A] bg-[#121212]"}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-2xl">{p.name}</h3>
                {p.featured && <span className="font-mono text-[10px] rounded-full bg-[#00FF9D] text-[#050505] px-2 py-1">MOST POPULAR</span>}
              </div>
              <p className="mt-2 text-sm text-[#A3A3A3]">{p.blurb}</p>
              <div className="mt-8"><PriceTag monthly={p.price} period={period} big="text-5xl" testid={`pricing-price-${p.id}`} /></div>
              <ul className="mt-8 space-y-3 text-sm flex-1">
                {p.perks.map((k) => <li key={k} className="flex gap-3"><Check className="text-[#00FF9D] mt-0.5" weight="bold" />{k}</li>)}
              </ul>
              <Link to="/sign-up" data-testid={`pricing-${p.id}-cta`} onClick={() => trackHero("cta")}
                className={`mt-10 rounded-full py-3 text-center font-semibold transition-colors ${p.featured ? "bg-[#00FF9D] text-[#050505] hover:bg-white" : "border border-[#2A2A2A] hover:border-[#00FF9D] hover:text-[#00FF9D]"}`}>
                Start 14-Day Free Trial
              </Link>
            </div>
          ))}
        </div>
        <SecurityBlock />
      </div>
    </section>
  );
};

export default function Landing({ section }) {
  const [copy, setCopy] = useState(() => HERO_COPY[heroVariant()]);
  const [ready, setReady] = useState(() => !!cachedWinner());
  const [quotes, setQuotes] = useState([]);
  usePageMeta(PAGE_META[section || "home"]);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 2000);
    const target = section || window.location.hash.slice(1);
    fetchLanding()
      .then((d) => { if (d.hero_winner) setCopy(HERO_COPY[d.hero_winner]); setQuotes(d.quotes || []); })
      .catch(() => {})
      .finally(() => {
        setReady(true);
        trackHero("view");
        if (target) setTimeout(() => document.getElementById(target)?.scrollIntoView(), 50);
      });
    return () => clearTimeout(t);
  }, [section]);
  return (
    <div className="bg-[#050505] min-h-screen" data-testid="landing-page">
      <SiteNav home />
      <Hero copy={copy} ready={ready} />
      <Walkthrough />
      <Features />
      <QuotesBlock quotes={quotes} />
      <Pricing />
      <Faq />
      <section className="mx-auto max-w-7xl px-6 py-24 flex flex-col md:flex-row md:items-end justify-between gap-8">
        <h2 className="font-display text-3xl md:text-5xl font-semibold max-w-2xl">Calibrate your runway. <span className="text-[#00FF9D]">Fuel your growth.</span></h2>
        <Cta testid="footer-cta-btn" />
      </section>
      <SiteFooter />
    </div>
  );
}
